/**
 * sentryRiskAdvisory — derives the one-line current-advisory read from the typed
 * risk-pin set (NO-FALLBACK: the number is always the REAL pin count, never a
 * hand-written figure). Kept pure + separate so the view assembles the sentence
 * through i18next and tests can assert the derivation deterministically.
 *
 * Hazard/severity LABELS are sourced from `sentryWatch` (the canonical,
 * TOTAL-over-HazardType hazard copy and the governed 3-tier map legend) via
 * `riskLabelCopy()` rather than a second, narrower `sentryRisk`-local
 * hazard/severity vocabulary — one fewer severity vocabulary to keep in sync.
 *
 */
import type { HazardType } from "@/api/sentry";
import type {
  ActiveHazardSeverity,
  PropertyRiskPin,
} from "@/api/propertyHazardReport";
import type { CommonTranslations } from "@/i18n/types";

const SEVERITY_RANK: Record<ActiveHazardSeverity, number> = {
  elevated: 0,
  high: 1,
  severe: 2,
};

export interface RiskAdvisoryRead {
  hazardType: HazardType;
  severity: ActiveHazardSeverity;
  /** Number of stands carrying the dominant hazard at the top severity. */
  standCount: number;
}

/** Stable identity shared by map features, selections, and advisory lookup. */
export function riskFeatureId(pin: {
  standId: string;
  hazardType: string;
}): string {
  return `risk:${pin.standId}:${pin.hazardType}`;
}

/**
 * Localized hazard + severity labels shared by every risk-pin rendering
 * surface (map pins, right-inspector, ranked-list advisory sentence). Built
 * from `sentryWatch` copy — TOTAL over the canonical 5-member `HazardType`
 * and the governed low/moderate/critical map legend — instead of duplicating
 * a second hazard/severity translation block scoped to `sentryRisk`.
 */
export interface RiskLabelCopy {
  noRisks: string;
  advisoryTemplate: string;
  hazard: Record<HazardType, string>;
  severity: Record<ActiveHazardSeverity, string>;
}

export function riskLabelCopy(common: CommonTranslations): RiskLabelCopy {
  return {
    noRisks: common.sentryRisk.noRisks,
    advisoryTemplate: common.sentryRisk.advisoryTemplate,
    hazard: common.sentryWatch.hazard,
    severity: {
      elevated: common.sentryWatch.map.legend.low,
      high: common.sentryWatch.map.legend.moderate,
      severe: common.sentryWatch.map.legend.critical,
    },
  };
}

export function riskHazardLabel(hazard: HazardType, copy: RiskLabelCopy): string {
  return copy.hazard[hazard];
}

export function riskSeverityLabel(
  severity: ActiveHazardSeverity,
  copy: RiskLabelCopy,
): string {
  return copy.severity[severity];
}

/**
 * Resolve the highest-severity risk group: the dominant hazard among the pins at
 * the maximum observed severity, plus how many stands carry it. Returns null when
 * there are no pins (the view then shows the explicit "no active risks" read).
 */
export function deriveRiskAdvisory(
  pins: PropertyRiskPin[],
): RiskAdvisoryRead | null {
  if (pins.length === 0) return null;

  const topSeverity = pins.reduce<ActiveHazardSeverity>(
    (max, pin) =>
      SEVERITY_RANK[pin.severity] > SEVERITY_RANK[max] ? pin.severity : max,
    "elevated",
  );

  const atTop = pins.filter((pin) => pin.severity === topSeverity);

  // Dominant hazard at the top severity (most stands wins; stable on ties).
  const counts = new Map<HazardType, number>();
  for (const pin of atTop) {
    counts.set(pin.hazardType, (counts.get(pin.hazardType) ?? 0) + 1);
  }
  let hazardType = atTop[0].hazardType;
  let standCount = 0;
  for (const [hazard, count] of counts) {
    if (count > standCount) {
      hazardType = hazard;
      standCount = count;
    }
  }

  return { hazardType, severity: topSeverity, standCount };
}

/** Assemble localized advisory scope from typed served pins, never raw backend prose. */
export function formatRiskAdvisory(
  pins: PropertyRiskPin[],
  copy: RiskLabelCopy,
): string {
  const read = deriveRiskAdvisory(pins);
  if (!read) return copy.noRisks;
  return copy.advisoryTemplate
    .replace("{hazard}", riskHazardLabel(read.hazardType, copy))
    .replace("{count}", String(read.standCount))
    .replace("{severity}", riskSeverityLabel(read.severity, copy));
}
